import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, CalendarDays, Copy, Facebook, Instagram, Linkedin, Send, Twitter } from "lucide-react";
import { toast } from "sonner";
import { Toaster } from "@/components/ui/sonner";
import { Chip, SubHeader } from "./FeatureShowcasePage";
import { Footer } from "./Footer";
import { NotFound } from "./NotFound";

const API = process.env.REACT_APP_BACKEND_URL;

const fetchJson = (url) =>
    fetch(url).then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))));

const fmtDate = (iso, lang) =>
    new Date(iso).toLocaleDateString(lang === "el" ? "el-GR" : "en-GB", { day: "numeric", month: "long", year: "numeric" });

const catLabel = (t, c) => t.news.filters[c] || c;

export const NewsCard = ({ post, t, lang, testId, light = true }) => {
    const isIg = post.type === "instagram";
    const isFb = (post.instagramUrl || "").includes("facebook.com");
    const SocialIcon = isFb ? Facebook : Instagram;
    return (
        <motion.a
            href={`/nea/${post.slug}`}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.2 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className={`group flex flex-col overflow-hidden rounded-3xl border transition-colors duration-500 ${
                light
                    ? "border-[#E2E8F0] bg-white shadow-sm hover:border-[#0D9488]/50 hover:shadow-md"
                    : "border-white/[0.08] bg-[#093448]/60 hover:border-[#22A6C0]/50"
            }`}
            data-testid={testId}
        >
            <div className="relative overflow-hidden" style={{ aspectRatio: "16 / 9" }}>
                {post.image ? (
                    <img
                        src={post.image}
                        alt={post.title[lang]}
                        loading="lazy"
                        className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-[1.04]"
                    />
                ) : (
                    <div className={`flex h-full w-full items-center justify-center ${light ? "bg-gradient-to-br from-[#E2E8F0] to-[#F1F5F9]" : "bg-gradient-to-br from-[#0D4159] to-[#093448]"}`}>
                        {isIg ? <SocialIcon size={28} className={light ? "text-[#0D9488]" : "text-[#22A6C0]"} /> : <img src="/brand/app-icon-256.webp" alt="" className="h-14 w-14 opacity-60" />}
                    </div>
                )}
                <span className="absolute left-4 top-4 rounded-full border border-white/15 bg-[#052536]/75 px-3.5 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-[#F7EAB8] backdrop-blur-md">
                    {catLabel(t, post.category)}
                </span>
                {isIg && (
                    <span className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full border border-white/15 bg-[#052536]/75 text-[#F2F5F6] backdrop-blur-md">
                        <SocialIcon size={16} />
                    </span>
                )}
            </div>
            <div className="flex flex-1 flex-col p-6 md:p-7">
                <h3 className={`text-lg font-bold leading-snug transition-colors duration-300 md:text-xl ${
                    light ? "text-[#0F172A] group-hover:text-[#0D9488]" : "text-[#F2F5F6] group-hover:text-[#F7EAB8]"
                }`}>
                    {post.title[lang]}
                </h3>
                <p className={`mt-3 flex-1 text-sm leading-relaxed ${light ? "text-[#475569]" : "text-[#A3B3B8]"}`}>{post.excerpt[lang]}</p>
                <div className={`mt-6 flex items-center justify-between text-xs ${light ? "text-[#64748B]" : "text-[#697B80]"}`}>
                    <span className="flex items-center gap-2">
                        <CalendarDays size={14} />
                        {fmtDate(post.date, lang)}
                    </span>
                    <span className={`flex items-center gap-1.5 font-bold ${light ? "text-[#0D9488]" : "text-[#4CC9DC]"}`}>
                        {t.news.readMore}
                        <ArrowUpRight size={14} className="transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                    </span>
                </div>
            </div>
        </motion.a>
    );
};

export const NewsPage = ({ t, lang, onLangChange }) => {
    const [filter, setFilter] = useState("all");
    const [posts, setPosts] = useState(null);

    useEffect(() => {
        let alive = true;
        fetchJson(`${API}/api/news`)
            .then((data) => alive && setPosts(data))
            .catch(() => alive && setPosts([]));
        return () => {
            alive = false;
        };
    }, []);

    const sorted = posts ? [...posts].sort((a, b) => b.date.localeCompare(a.date)) : [];
    const shown = filter === "all" ? sorted : sorted.filter((p) => p.category === filter);

    useEffect(() => {
        document.title = t.news.metaTitle;
        const desc = document.querySelector('meta[name="description"]');
        if (desc) desc.setAttribute("content", t.news.metaDesc);
        const canonical = document.querySelector('link[rel="canonical"]');
        if (canonical) canonical.setAttribute("href", "https://theraoffice.gr/nea");
        window.scrollTo(0, 0);
    }, [t.news.metaTitle, t.news.metaDesc]);

    const filters = [
        { id: "all", label: t.news.filters.all },
        { id: "article", label: t.news.filters.article },
        { id: "news", label: t.news.filters.news },
        { id: "instagram", label: t.news.filters.instagram },
        { id: "guide", label: t.news.filters.guide },
    ];

    return (
        <div data-testid="news-page">
            <SubHeader t={t} lang={lang} onLangChange={onLangChange} />
            <main className="pt-36 md:pt-44">
                <div className="relative mx-auto max-w-[1400px] px-6 md:px-12">
                    <motion.div
                        initial={{ opacity: 0, y: 30 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                        className="relative"
                    >
                        <h1 className="font-display text-5xl leading-tight tracking-tight text-[#0F172A] md:text-6xl" data-testid="news-title">
                            {t.news.title}
                        </h1>
                        <p className="mt-5 max-w-2xl text-base leading-relaxed text-[#475569] md:text-lg">{t.news.subtitle}</p>
                    </motion.div>
                    <div className="mt-10 flex flex-wrap gap-2.5 border-t border-[#E2E8F0] pt-9" data-testid="news-filters">
                        {filters.map((f) => (
                            <Chip key={f.id} active={filter === f.id} onClick={() => setFilter(f.id)} testId={`news-filter-${f.id}`}>
                                {f.label}
                            </Chip>
                        ))}
                    </div>
                </div>

                <div className="mx-auto max-w-[1400px] px-6 pb-28 pt-12 md:px-12">
                    {posts === null ? (
                        <div className="grid grid-cols-1 gap-7 md:grid-cols-2 lg:grid-cols-3" data-testid="news-loading">
                            {[0, 1, 2].map((i) => (
                                <div key={i} className="h-80 animate-pulse rounded-3xl border border-[#E2E8F0] bg-white/60" />
                            ))}
                        </div>
                    ) : shown.length === 0 ? (
                        <p className="py-16 text-center text-sm text-[#64748B]" data-testid="news-empty">{t.news.empty}</p>
                    ) : (
                        <div className="grid grid-cols-1 gap-7 md:grid-cols-2 lg:grid-cols-3" data-testid="news-grid">
                            {shown.map((p) => (
                                <NewsCard key={p.slug} post={p} t={t} lang={lang} testId={`news-card-${p.slug}`} />
                            ))}
                        </div>
                    )}
                </div>
            </main>
            <Footer t={t} />
            <Toaster position="bottom-center" theme="dark" />
        </div>
    );
};

const setMeta = (name, content, prop) => {
    let el = document.querySelector(prop ? `meta[property="${name}"]` : `meta[name="${name}"]`);
    if (!el) {
        el = document.createElement("meta");
        el.setAttribute(prop ? "property" : "name", name);
        document.head.appendChild(el);
    }
    el.setAttribute("content", content);
};

// Returns "instagram" | "facebook" | "facebook-video" when the URL is an
// embeddable post, otherwise null (caller renders a preview-card fallback).
const socialKind = (url) => {
    if (!url) return null;
    if (/instagram\.com\/(p|reel|tv)\//.test(url)) return "instagram";
    if (/facebook\.com\//.test(url)) {
        if (/(\/videos|\/reel)/.test(url)) return "facebook-video";
        if (/(\/posts\/|\/photos|\/permalink|story_fbid|\/share\/)/.test(url)) return "facebook";
    }
    return null;
};

const InstagramEmbed = ({ url }) => {
    useEffect(() => {
        const process = () => window.instgrm?.Embeds?.process();
        const existing = document.querySelector('script[src*="instagram.com/embed.js"]');
        if (existing) {
            process();
        } else {
            const s = document.createElement("script");
            s.src = "https://www.instagram.com/embed.js";
            s.async = true;
            s.onload = process;
            document.body.appendChild(s);
        }
    }, [url]);
    return (
        <div className="overflow-hidden rounded-3xl border border-white/10 bg-white p-1" data-testid="instagram-embed">
            <blockquote
                className="instagram-media"
                data-instgrm-permalink={url}
                data-instgrm-version="14"
                style={{ margin: "0 auto", maxWidth: 540, width: "100%", background: "#FFF" }}
            >
                <a href={url} target="_blank" rel="noopener noreferrer">{url}</a>
            </blockquote>
        </div>
    );
};

const FacebookEmbed = ({ url, video }) => {
    const plugin = video ? "video.php" : "post.php";
    const src = `https://www.facebook.com/plugins/${plugin}?href=${encodeURIComponent(url)}&show_text=true&width=500`;
    return (
        <div className="overflow-hidden rounded-3xl border border-white/10 bg-white" data-testid="facebook-embed">
            <iframe
                src={src}
                width="500"
                height={video ? 500 : 680}
                style={{ border: "none", overflow: "hidden", width: "100%", maxWidth: 500, display: "block", margin: "0 auto" }}
                scrolling="no"
                allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                allowFullScreen
                title="Facebook post"
            />
        </div>
    );
};

const SocialBody = ({ post, t, lang }) => {
    const url = post.instagramUrl || "";
    const kind = socialKind(url);
    const isFb = kind === "facebook" || kind === "facebook-video" || (!kind && url.includes("facebook.com"));
    const SocialIcon = isFb ? Facebook : Instagram;
    const network = isFb ? "Facebook" : "Instagram";
    return (
        <div className="mt-10" data-testid="social-body">
            {kind === "instagram" && <InstagramEmbed url={url} />}
            {(kind === "facebook" || kind === "facebook-video") && <FacebookEmbed url={url} video={kind === "facebook-video"} />}
            {!kind && (
                <div className="overflow-hidden rounded-3xl border border-[#E2E8F0] bg-white shadow-sm">
                    {post.image && <img src={post.image} alt={post.title[lang]} className="aspect-video w-full object-cover" />}
                    <div className="flex items-center gap-3 bg-[#F8FAFC] p-5 text-[#475569]">
                        <SocialIcon size={20} className="text-[#0D9488]" />
                        <span className="text-sm">{network}</span>
                    </div>
                </div>
            )}
            {post.excerpt[lang] && (
                <p className="mt-8 text-base leading-[1.85] text-[#475569] md:text-lg" data-testid="social-caption">{post.excerpt[lang]}</p>
            )}
            {url && (
                <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-8 inline-flex items-center gap-2.5 rounded-full bg-[#032A44] px-7 py-3.5 text-sm font-bold text-white transition-colors duration-300 hover:bg-[#01537B]"
                    data-testid="social-cta"
                >
                    <SocialIcon size={16} />
                    {t.news.viewOriginal} {network}
                </a>
            )}
        </div>
    );
};

export const NewsArticlePage = ({ slug, t, lang, onLangChange }) => {
    const [post, setPost] = useState(undefined); // undefined = loading, null = not found
    const [related, setRelated] = useState([]);
    const isEl = lang === "el";
    const canonical = `https://theraoffice.gr/nea/${slug}`;

    useEffect(() => {
        let alive = true;
        setPost(undefined);
        fetchJson(`${API}/api/news/${encodeURIComponent(slug)}`)
            .then((data) => {
                if (!alive) return;
                setPost(data);
                fetchJson(`${API}/api/news`)
                    .then((all) => {
                        if (alive) setRelated(all.filter((p) => p.type === "article" && p.slug !== slug).slice(0, 3));
                    })
                    .catch(() => {});
            })
            .catch(() => alive && setPost(null));
        return () => {
            alive = false;
        };
    }, [slug]);

    useEffect(() => {
        if (!post) return;
        document.title = isEl ? post.seo.titleEl : post.seo.titleEn;
        const description = isEl ? post.seo.descEl : post.seo.descEn;
        setMeta("description", description);
        setMeta("og:title", post.title[lang], true);
        setMeta("og:description", description, true);
        setMeta("og:image", `https://theraoffice.gr${post.image}`, true);
        setMeta("og:type", "article", true);
        setMeta("og:url", canonical, true);
        const canonicalEl = document.querySelector('link[rel="canonical"]');
        if (canonicalEl) canonicalEl.setAttribute("href", canonical);
        const script = document.createElement("script");
        script.type = "application/ld+json";
        script.text = JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: post.title[lang],
            description,
            datePublished: post.date,
            inLanguage: lang,
            image: `https://theraoffice.gr${post.image}`,
            mainEntityOfPage: canonical,
            author: { "@type": "Organization", name: "TheraOffice", url: "https://theraoffice.gr/" },
            publisher: { "@type": "Organization", name: "TheraOffice", logo: { "@type": "ImageObject", url: "https://theraoffice.gr/brand/app-icon-256.png" } },
        });
        document.head.appendChild(script);
        window.scrollTo(0, 0);
        return () => script.remove();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [post, slug, lang]);

    if (post === undefined) {
        return (
            <div data-testid="article-loading">
                <SubHeader t={t} lang={lang} onLangChange={onLangChange} />
                <main className="mx-auto max-w-3xl px-6 pb-32 pt-40 md:px-12">
                    <div className="h-8 w-40 animate-pulse rounded-full bg-[#E2E8F0]" />
                    <div className="mt-8 h-12 w-3/4 animate-pulse rounded-2xl bg-[#E2E8F0]" />
                    <div className="mt-10 aspect-video w-full animate-pulse rounded-[2rem] bg-[#E2E8F0]" />
                </main>
                <Footer t={t} />
            </div>
        );
    }

    if (!post) {
        return (
            <div className="App grain">
                <NotFound />
            </div>
        );
    }

    const shareText = encodeURIComponent(post.title[lang]);
    const shareUrl = encodeURIComponent(canonical);
    const shares = [
        { id: "facebook", icon: Facebook, href: `https://www.facebook.com/sharer/sharer.php?u=${shareUrl}` },
        { id: "x", icon: Twitter, href: `https://twitter.com/intent/tweet?url=${shareUrl}&text=${shareText}` },
        { id: "whatsapp", icon: Send, href: `https://wa.me/?text=${shareText}%20${shareUrl}` },
        { id: "linkedin", icon: Linkedin, href: `https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}` },
    ];

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(canonical);
            toast.success(t.news.copied);
        } catch {
            toast.error(canonical);
        }
    };

    return (
        <div data-testid="article-page">
            <SubHeader t={t} lang={lang} onLangChange={onLangChange} />
            <main className="pt-32 md:pt-40">
                <article className="mx-auto max-w-3xl px-6 md:px-12">
                    <a
                        href="/nea"
                        className="flex w-fit items-center gap-2 text-sm font-semibold text-[#0D9488] transition-colors duration-300 hover:text-[#D97706]"
                        data-testid="article-back"
                    >
                        <ArrowLeft size={16} />
                        {t.news.back}
                    </a>
                    <div className="mt-8 flex flex-wrap items-center gap-4 text-xs text-[#64748B]">
                        <span className="rounded-full border border-[#D97706]/30 bg-[#FEF3C7] px-3.5 py-1.5 font-bold uppercase tracking-[0.14em] text-[#B45309]">
                            {catLabel(t, post.category)}
                        </span>
                        <span className="flex items-center gap-2">
                            <CalendarDays size={14} />
                            {fmtDate(post.date, lang)}
                        </span>
                    </div>
                    <h1 className="font-display mt-6 text-4xl leading-[1.15] tracking-tight text-[#0F172A] md:text-5xl" data-testid="article-title">
                        {post.title[lang]}
                    </h1>
                    {post.image && post.type === "article" && (
                        <motion.figure
                            initial={{ opacity: 0, y: 24 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                            className="mt-10 overflow-hidden rounded-[2rem] border border-white/10"
                        >
                            <img src={post.image} alt={post.title[lang]} className="h-auto w-full object-cover" data-testid="article-image" />
                        </motion.figure>
                    )}

                    {post.type === "instagram" ? (
                        <SocialBody post={post} t={t} lang={lang} />
                    ) : (
                        <div className="article-content mt-10" data-testid="article-body" dangerouslySetInnerHTML={{ __html: post.body[lang] }} />
                    )}

                    <div className="mt-14 flex flex-wrap items-center gap-3 border-t border-[#E2E8F0] pt-8" data-testid="article-share">
                        <span className="mr-2 text-xs font-bold uppercase tracking-[0.2em] text-[#64748B]">{t.news.share}</span>
                        {shares.map(({ id, icon: Icon, href }) => (
                            <a
                                key={id}
                                href={href}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label={`Share on ${id}`}
                                className="flex h-11 w-11 items-center justify-center rounded-full border border-[#E2E8F0] bg-white text-[#475569] transition-colors duration-300 hover:border-[#0D9488] hover:text-[#0D9488]"
                                data-testid={`share-${id}`}
                            >
                                <Icon size={17} />
                            </a>
                        ))}
                        <button
                            onClick={copyLink}
                            aria-label={t.news.copyLink}
                            className="flex h-11 w-11 items-center justify-center rounded-full border border-[#E2E8F0] bg-white text-[#475569] transition-colors duration-300 hover:border-[#0D9488] hover:text-[#0D9488]"
                            data-testid="share-copy"
                        >
                            <Copy size={17} />
                        </button>
                    </div>
                </article>

                {related.length > 0 && (
                    <div className="mx-auto max-w-[1400px] px-6 pb-28 pt-20 md:px-12">
                        <h2 className="font-display mb-10 text-3xl italic tracking-tight text-[#0E4B57]" data-testid="related-title">
                            {t.news.related}
                        </h2>
                        <div className="grid grid-cols-1 gap-7 md:grid-cols-3" data-testid="related-grid">
                            {related.map((p) => (
                                <NewsCard key={p.slug} post={p} t={t} lang={lang} testId={`related-card-${p.slug}`} />
                            ))}
                        </div>
                    </div>
                )}
            </main>
            <Footer t={t} />
            <Toaster position="bottom-center" theme="dark" />
        </div>
    );
};
